import { PrinterIcon } from "@heroicons/react/24/outline";
import { Document, Font, Link, Page, StyleSheet, Text, usePDF, View } from "@react-pdf/renderer";
import TGButton from "src/components/ui/TGButton";

// Определяем типы для данных
interface MeetingPlan {
  ID: number;
  Hyperlink: string;
  Description: string;
  Responsible: string;
  Index: number;
}

interface MyDocumentProps {
  meetingPlans: MeetingPlan[];
  location: string;
  title: string;
  goal: string;
  owner: string;
  participants: string;
  period: string;
  introduction: string;
  results: string;
  kpi: string;
}
// Register Font
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf",
      fontWeight: "light",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf",
      fontWeight: "medium",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
      fontWeight: "bold",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf",
      fontStyle: "italic",
    },
  ],
});

// 1. Создаем стили для PDF-документа
const styles = StyleSheet.create({
  page: {
    fontFamily: "Roboto",
    fontWeight: "normal",
    width: "934px",
    fontSize: 14,
  },
  wrapTitle: {
    backgroundColor: "#012060",
  },
  title: {
    fontSize: 24,
    padding: 10,
    textTransform: "uppercase",
    fontWeight: "bold",
    textAlign: "center",
    color: "#FFFFFF",
  },
  locationTitle: {
    textAlign: "center",
    fontSize: 18,
    padding: 10,
    fontStyle: "italic",
  },
  locationSmallTitle: {
    fontSize: 14,
  },
  tableH: {
    fontSize: 16,
    paddingLeft: 10,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  tableH2: {
    fontSize: 14,
    paddingLeft: 10,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  bg_gray: {
    backgroundColor: "#C5D9F1",
  },
  bg_yellow: {
    backgroundColor: "#FFFF00",
  },
  row: { display: "flex", flexDirection: "row" },
  column: { display: "flex", flexDirection: "column" },
  bRight: { borderRight: "1px solid #C1C1C1" },
  bBottom: { borderBottom: "1px solid #C1C1C1" },
  divideY: { borderBottomWidth: "0", borderTopWidth: "1px", borderTopColor: "#C1C1C1" },
  tableCol33: {
    width: "33%",
  },
});

// Компонент PDF-документа с типизацией пропсов
const MyDocument: React.FC<MyDocumentProps> = ({
  meetingPlans,
  location,
  title,
  goal,
  introduction,
  kpi,
  owner,
  participants,
  period,
  results,
}) => (
  <Document>
    <Page size="A3" style={styles.page}>
      <View style={styles.wrapTitle} key="h4">
        <Text style={styles.title}>Регламент совещания (ToR)</Text>
        <Text style={[styles.locationTitle, styles.bg_yellow]}>{location}</Text>
        <Text style={[styles.bg_gray, styles.tableH]}>1{"    "}CОВЕЩАНИЕ</Text>
        <Text style={[styles.locationTitle, styles.bg_yellow]}>{title}</Text>
      </View>
      <View>
        <Text style={[styles.bg_gray, styles.tableH]}>2{"    "}ЦЕЛЬ</Text>
        <Text style={{ paddingLeft: 10 }}>{goal}</Text>
      </View>
      <View style={[styles.row]}>
        <View style={[styles.column, styles.bRight, { flex: 1 }]}>
          <Text style={[styles.bg_gray, styles.tableH]}>3{"    "}ВЛАДЕЛЕЦ</Text>
          <Text style={{ paddingLeft: 10 }}>{owner}</Text>
        </View>
        <View style={[styles.column, styles.bRight, { flex: 1 }]}>
          <Text style={[styles.bg_gray, styles.tableH]}>4{"    "}УЧАСТНИКИ</Text>
          <Text style={{ paddingLeft: 10 }}>{participants}</Text>
        </View>
        <View style={[styles.column, { flex: 1 }]}>
          <Text style={[styles.bg_gray, styles.tableH]}>5{"    "}ВРЕМЯ И ПЕРИОДИЧНОСТЬ</Text>
          <Text style={{ paddingLeft: 10 }}>{period}</Text>
        </View>
      </View>
      <View style={[styles.row]}>
        <View style={[styles.column, styles.bRight, { flex: 1 }]}>
          <Text style={[styles.bg_gray, styles.tableH]}>6{"    "}ВВОДНЫЕ К ВСТРЕЧЕ</Text>
          <Text style={{ paddingLeft: 10 }}>{introduction}</Text>
        </View>
        <View style={[styles.column, styles.bRight, { flex: 1 }]}>
          <Text style={[styles.bg_gray, styles.tableH]}>7{"    "}ИТОГИ</Text>
          <Text style={{ paddingLeft: 10 }}>{results}</Text>
        </View>
        <View style={[styles.column, { flex: 1 }]}>
          <Text style={[styles.bg_gray, styles.tableH]}>8{"    "}УРОВЕНЬ KPI ОТЧЕТА</Text>
          <Text style={{ paddingLeft: 10 }}>{kpi}</Text>
        </View>
      </View>
      <View>
        <View style={[styles.row, styles.bBottom]}>
          <View style={[styles.column, { width: "4%" }]}>
            <Text style={[styles.bg_gray, styles.tableH]}>9</Text>
          </View>
          <View style={[styles.column, { width: "32%" }]}>
            <Text style={[styles.bg_gray, styles.tableH]}>ПЛАН ВСТРЕЧИ</Text>
          </View>
          <View style={[styles.column, { width: "32%" }]}>
            <Text style={[styles.bg_gray, styles.tableH]}>ГИПЕРСЫЛКА</Text>
          </View>
          <View style={[styles.column, { width: "32%" }]}>
            <Text style={[styles.bg_gray, styles.tableH]}>ОТВЕТСТВЕННЫЙ</Text>
          </View>
        </View>
      </View>
      {meetingPlans.map(({ ID, Description, Hyperlink, Responsible, Index }) => (
        <View style={[styles.row, styles.bBottom]} key={ID}>
          <View style={[styles.column, styles.bRight, { width: "4%" }]}>
            <Text style={styles.tableH2}>{Index + 1}</Text>
          </View>
          <View style={[styles.column, styles.bRight, { width: "32%" }]}>
            <Text
              style={{
                paddingLeft: "10px",
                paddingRight: "10px",
              }}
            >
              {Description}
            </Text>
          </View>
          <View style={[styles.column, styles.bRight, { width: "32%" }]}>
            <Link
              href={Hyperlink}
              style={{
                paddingLeft: "10px",
                paddingRight: "10px",
              }}
            >
              {Hyperlink}
            </Link>
          </View>
          <View style={[styles.column, { width: "32%" }]}>
            <Text
              style={{
                paddingLeft: "10px",
                paddingRight: "10px",
              }}
            >
              {Responsible}
            </Text>
          </View>
        </View>
      ))}
    </Page>
  </Document>
);

export function DownloadPDFButton({ data }: { data: MyDocumentProps }) {
  const [instance, updateInstance] = usePDF({
    document: <MyDocument {...data} />,
  });

  const handleDownload = () => {
    if (instance.url) {
      // Программно создаём элемент <a> для скачивания
      const link = document.createElement("a");
      link.href = instance.url;
      link.download = "test.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <TGButton icon={<PrinterIcon className="text-darkblue" />} onClick={handleDownload}>
      Распечатать
    </TGButton>
  );
}
